import type { ComponentProps } from 'react'
import { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import GeoContext from 'contexts/geoContext'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import noop from 'lib/function/noop'
import uuidV4 from 'lib/string/uuidV4Utils'
import SearchPlaceSelect from 'components/Search/SearchPlaceSelect/SearchPlaceSelect'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchExperienceById } from 'actions/ExperienceActions'
import { setTypeaheadUserSelectedDates } from 'actions/SearchActions'
import { getSearchItemExperienceId } from 'lib/search/searchItem'
import { getCurrentUserId } from 'selectors/accountSelectors'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_PLACE_ID,
  DATE_SEARCH_OPTION_IDS,
} from 'constants/search'
import usePrevious from 'hooks/usePrevious'
import { useLocalizedSearchItems } from 'hooks/usePlace'
import { getTypeaheadSuggestionsV2 } from 'lib/search/searchUtils'
import { take } from 'lib/array/arrayUtils'
import moment from 'moment'
import { useExtractDatesAndOccupancy } from 'hooks/Search/useExtractDatesAndOccupancy'
import { areOccupancyArraysEqual } from 'lib/offer/occupancyUtils'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import { DEFAULT_FILTER } from 'home/components/TrendingDestinations/TrendingDestinations'
import config from 'constants/config'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import {
  getSearchDateSelectionFromExtractedDates,
  getSearchDateSelectionFromForm,
  isSearchDateSelectionExtractedOverride,
  resolveExtractCommittedPhraseState,
  shouldSkipForcedExtractApply,
  updateDateConfirmationStateOnExtract,
} from 'lib/search/typeaheadDateConfirmationUtils'

const MINIMUM_NUM_DESTINATIONS = 6

interface Props {
  placeTypes?: Array<App.SearchPlaceType>
  searchTypes?: Array<App.SearchType>
  placeholder?: string
  ['data-testid']?: string
  onChange?: (item?: App.SearchItem) => void
  className?: string
  label?: string
  variant: 'inline' | 'dropdown'
  dropdownSize?: ComponentProps<typeof SearchPlaceSelect>['dropdownSize']
  dropdownPortaled?: boolean
  inputRef?: React.Ref<HTMLInputElement>
  controlsContainerRef?: React.RefObject<HTMLDivElement | null>
  /** Whether or not this search input is for the secondary location on a search */
  isSecondaryLocation?: boolean
  /** Allow to provide your own set of placeholder items (often 'popular places') instead of taking from state */
  placeholderItems?: Array<App.SearchItem>
  /** A list of recent search items, will be displayed along with placeholder items */
  recentSearchItems?: Array<App.FullRecentSearch>
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>
  required?: boolean
  searchVertical?: App.SearchPlaceVertical
  onLocationToggle?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  updateSearchSessionId?: (newSessionId?: string) => void
  multiline?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
  allowFlexibleDateExtraction?: boolean
  userInteractions?: {
    dates: boolean
    travelers: boolean
    location?: boolean
  }
  setTravelDatesShowAIGlow?: (show: boolean) => void
  setOccupancyShowAIGlow?: (show: boolean) => void
  onPhraseChange?: (searchPhrase: string) => void
  onSearchPhraseChange?: (searchPhrase: string) => void
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  /** Called when extracted dates differ from the baseline and need user acknowledgement */
  onDateConfirmationRequired?: () => void
  /** Called when the NL phrase changes incompatibly so date ownership from the picker can reset */
  onIncompatibleSearchPhraseChange?: () => void
  /** The path to use for the search */
  searchPath?: string
  /** The path to use for the search map */
  searchMapPath?: string
}

/**
 * Common search control for setting search for a 'place'
 * Is already hard wired into the existing global search context
 * and will populate the 'searchItem' property
 */
function SearchLocationInput(props: Props) {
  const {
    searchTypes,
    placeholder,
    onChange = noop,
    className,
    label,
    placeTypes,
    variant,
    dropdownSize,
    dropdownPortaled,
    inputRef,
    isSecondaryLocation,
    required,
    searchVertical,
    recentSearchItems,
    overrideSearchItems,
    onLocationToggle,
    controlsContainerRef,
    updateSearchSessionId,
    multiline = false,
    showNaturalLanguageSearchSuggestions = false,
    allowFlexibleDateExtraction = true,
    userInteractions,
    setTravelDatesShowAIGlow,
    setOccupancyShowAIGlow,
    onPhraseChange,
    onSearchPhraseChange,
    temporarySearchSubmitRef,
    onDateConfirmationRequired,
    onIncompatibleSearchPhraseChange,
    searchPath,
    searchMapPath,
  } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [searchSessionId, setSearchSessionId] = useState<string>(uuidV4())
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const { currentRegionCode } = useContext(GeoContext)
  const searchState = useContext(GlobalSearchStateContext)
  const { popularDestinations } = searchState
  const searchItem = isSecondaryLocation ? searchState.secondarySearchItem : searchState.searchItem
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const leUserId = useAppSelector(getCurrentUserId)

  const lereDestinationModelEnabled: boolean = useFeatureFlag(OptimizelyFeatureFlags.lereDestinationModelEnabled)
  const hotelSearchSelected = searchVertical === 'hotel'
  const experienceSearchSelected = searchVertical === 'experience'

  const [trendingDestinations] = useHotelTrendingDestinations(
    DEFAULT_FILTER,
    currentRegionCode,
    leUserId,
    domainUserId,
    !(hotelSearchSelected && lereDestinationModelEnabled && config.BRAND == 'luxuryescapes'),
  )

  const trendingDestinationsAsSearchItems: Array<App.SearchItem> = useMemo(() =>
    trendingDestinations.map(destination => ({
      value: destination.destinationId,
      format: {
        mainText: destination.name,
      },
      imageId: destination.imageId,
      searchType: 'destination',
    } as App.SearchDestination)),
  [trendingDestinations],
  )

  // Extract dates and occupancy from typeahead streaming data
  const typeaheadId = temporarySearchSubmitRef?.current?.typeaheadId
  const { extractedDates, extractedOccupancy } = useExtractDatesAndOccupancy(typeaheadId)
  const extractSourceInput = useAppSelector((state) => {
    if (!typeaheadId) return undefined
    const stream = state.search.typeaheadStreamingData[typeaheadId]
    // Prefer the input tied to the current travelDates chunk. completedInput can remain
    // from a prior extract (e.g. temporaryDoNotExtract typing starts preserve it), which
    // incorrectly makes a newer date extract look stale vs the current searchPhrase and
    // blocks soft-confirm / late date updates after replacing dates in the query.
    return stream?.travelDates?.input ?? stream?.completedInput
  })
  const lastForcedExtractKeyRef = useRef<string | undefined>(undefined)
  // Remembers an NL phrase whose dates were already committed (recent search / URL).
  // Survives the clear-selection race where searchItem is cleared before searchPhrase.
  const committedSelectionPhraseRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const selectedPhrase = searchState.searchItem?.format?.mainText
    if (
      selectedPhrase
      && selectedPhrase === searchPhrase
      && (searchState.checkinDate || searchState.flexibleMonths)
    ) {
      committedSelectionPhraseRef.current = searchPhrase
    }
  }, [searchState.searchItem, searchPhrase, searchState.checkinDate, searchState.flexibleMonths])

  useEffect(() => {
    if (!showNaturalLanguageSearchSuggestions) {
      return
    }

    const extractedDateSelection = getSearchDateSelectionFromExtractedDates(extractedDates)
    const shouldApplyExtractedDates = isSearchDateSelectionExtractedOverride(extractedDateSelection)

    if (shouldApplyExtractedDates) {
      const currentDateSelection = getSearchDateSelectionFromForm({
        checkinDate: searchState.checkinDate,
        checkoutDate: searchState.checkoutDate,
        flexibleNights: searchState.flexibleNights,
        flexibleMonths: searchState.flexibleMonths,
        isAnytimeDateSelected: searchState.isAnytimeDateSelected,
      })
      const refData = temporarySearchSubmitRef?.current
      const extractKey = JSON.stringify(extractedDateSelection)

      const committedPhraseState = resolveExtractCommittedPhraseState({
        searchPhrase,
        extractSourceInput,
        committedSelectionPhrase: committedSelectionPhraseRef.current,
        selectedItemMainText: searchState.searchItem?.format?.mainText,
        currentDateSelection,
        extractedDateSelection,
      })
      committedSelectionPhraseRef.current = committedPhraseState.nextCommittedSelectionPhrase
      if (committedPhraseState.shouldSkipExtract) {
        return
      }

      // Once the user edits the date picker, stop soft-confirm prompts and forced applies.
      if (userInteractions?.dates) {
        if (refData?.dateConfirmationRequired) {
          refData.dateConfirmationRequired = false
          refData.dateConfirmationViewed = true
        }
      } else {
        if (updateDateConfirmationStateOnExtract(refData ?? undefined, {
          searchPhrase,
          currentDateSelection,
          extractedDateSelection,
        })) {
          onDateConfirmationRequired?.()
        }

        if (shouldSkipForcedExtractApply({
          userHasEditedDates: false,
          lastForcedExtractKey: lastForcedExtractKeyRef.current,
          extractKey,
          currentDateSelection,
          extractedDateSelection,
        })) {
          // already applied this extract key while form dates diverge
        } else if (extractedDates?.checkinDate && extractedDates?.checkoutDate) {
          const newCheckinDate = moment(extractedDates.checkinDate)
          const newCheckoutDate = moment(extractedDates.checkoutDate)
          const currentCheckinDate = searchState.checkinDate
          const currentCheckoutDate = searchState.checkoutDate

          if (
            !currentCheckinDate?.isSame(newCheckinDate) ||
            !currentCheckoutDate?.isSame(newCheckoutDate)
          ) {
            searchDispatch({
              type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION,
            })
            searchDispatch({
              type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS,
            })
            searchDispatch({
              type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_CHECKIN_DATE,
              date: newCheckinDate,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
              date: newCheckoutDate,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
              optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC,
            })

            lastForcedExtractKeyRef.current = extractKey
            setTravelDatesShowAIGlow?.(true)
          }
        } else if (allowFlexibleDateExtraction && extractedDates?.flexibleMonths && extractedDates?.flexibleNights) {
          const newFlexibleMonths = extractedDates.flexibleMonths
          const newFlexibleNights = extractedDates.flexibleNights as FLEXIBLE_DURATION_RANGE
          const currentFlexibleMonths = searchState.flexibleMonths
          const currentFlexibleNights = searchState.flexibleNights

          if (
            currentFlexibleMonths !== newFlexibleMonths ||
            currentFlexibleNights !== newFlexibleNights
          ) {
            searchDispatch({
              type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
              flexibleMonths: newFlexibleMonths,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
              flexibleNights: newFlexibleNights,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
              userSelectedFlexibleMonths: true,
            })
            searchDispatch({
              type: GlobalSearchStateActions.UNSET_CHECKIN_DATE,
            })
            searchDispatch({
              type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE,
            })
            searchDispatch({
              type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
              optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
            })

            lastForcedExtractKeyRef.current = extractKey
            setTravelDatesShowAIGlow?.(true)
          }
        }
      }
    } else {
      lastForcedExtractKeyRef.current = undefined
    }

    if (!userInteractions?.travelers && extractedOccupancy?.occupancies) {
      const newOccupancies = extractedOccupancy.occupancies
      const currentOccupancies = searchState.occupancies

      if (!areOccupancyArraysEqual(currentOccupancies, newOccupancies)) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_OCCUPANCIES,
          occupancies: newOccupancies,
        })

        setOccupancyShowAIGlow?.(true)
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedDates, extractedOccupancy, extractSourceInput, allowFlexibleDateExtraction, temporarySearchSubmitRef, searchPhrase, searchState.checkinDate, searchState.checkoutDate, searchState.flexibleNights, searchState.flexibleMonths, searchState.isAnytimeDateSelected, searchState.searchItem, searchDispatch, setTravelDatesShowAIGlow, setOccupancyShowAIGlow, userInteractions?.travelers, userInteractions?.dates, onDateConfirmationRequired])

  // Use personalized trending destinations if available (for hotel and experiences searches), otherwise fall back to context popularDestinations
  const usePersonalizedTrendingDestinations = (hotelSearchSelected || experienceSearchSelected) && trendingDestinationsAsSearchItems.length >= MINIMUM_NUM_DESTINATIONS
  const popularItems = usePersonalizedTrendingDestinations ? trendingDestinationsAsSearchItems : popularDestinations

  const filteredPopularItems = useMemo(() => {
    const filteredItems = popularItems.filter(item => item.value !== ANYWHERE_PLACE_ID)
    if (showNaturalLanguageSearchSuggestions && filteredItems.length === 0) {
      return take(getTypeaheadSuggestionsV2(currentRegionCode), 3)
    }
    return filteredItems
  }, [currentRegionCode, popularItems, showNaturalLanguageSearchSuggestions])

  const localizedPopularItems = useLocalizedSearchItems(filteredPopularItems)

  const onLocationSelect = useCallback((
    searchItem?: App.SearchItem,
  ) => {
    if (!searchItem) {
      searchDispatch({
        type: isSecondaryLocation ? GlobalSearchStateActions.UNSET_SECONDARY_SEARCH_ITEM : GlobalSearchStateActions.UNSET_SEARCH_ITEM,
      })
    } else {
      const experienceId = getSearchItemExperienceId(searchItem)
      if (experienceId) {
        dispatch(fetchExperienceById(experienceId))
      }

      searchDispatch({
        type: isSecondaryLocation ? GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM : GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem,
      })

      updateSearchSessionId?.(searchSessionId)
    }
    onChange(searchItem)

    // Reset search session ID to ensure a new session starts when an item is selected
    if (searchItem) {
      setSearchSessionId(uuidV4())
    }
  }, [onChange, searchDispatch, isSecondaryLocation, updateSearchSessionId, searchSessionId, dispatch])

  const onResultsChange = useCallback((results: Array<App.SearchItem>, searchPhrase: string) => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
      searchItems: results,
    })

    if (searchPhrase) {
      updateSearchSessionId?.(searchSessionId)
    }
  }, [searchDispatch, updateSearchSessionId, searchSessionId])

  const handleClear = useCallback(() => {
    setSearchSessionId(uuidV4())
  }, [])

  const previousSearchPhrase = usePrevious(searchPhrase)

  // When search phrase changes, we need to check if the previous search phrase is compatible with the current one.
  // If not, we reset the search session ID to ensure that the new search is treated as a fresh session.
  // Compatibility is defined as either the previous search phrase being a prefix of the current one,
  // or the current search phrase being a prefix of the previous one.
  // This is to ensure that we do not mix search sessions with incompatible search phrases.
  useEffect(() => {
    if (!previousSearchPhrase || !searchPhrase) return

    // for compatibility, either the previous search phrase is prefix of the current one,
    // or the current search phrase is prefix of the previous one
    const isPreviousPrefix = searchPhrase.length > 0 && previousSearchPhrase.startsWith(searchPhrase)
    const isCurrentPrefix = previousSearchPhrase.length > 0 && searchPhrase.startsWith(previousSearchPhrase)

    if (!isPreviousPrefix && !isCurrentPrefix) {
      setSearchSessionId(uuidV4())
    }
  }, [searchPhrase, previousSearchPhrase])

  const handleSearchPhraseChange = useCallback((searchPhrase: string) => {
    const refData = temporarySearchSubmitRef?.current
    if (refData) {
      const previousConfirmationPhrase = refData.dateConfirmationPhrase
      const isPreviousPrefix = !!previousConfirmationPhrase && previousConfirmationPhrase.startsWith(searchPhrase)
      const isCurrentPrefix = !!previousConfirmationPhrase && searchPhrase.startsWith(previousConfirmationPhrase)

      if (!previousConfirmationPhrase || (!isPreviousPrefix && !isCurrentPrefix)) {
        refData.dateConfirmationPhrase = searchPhrase
        refData.dateConfirmationBaseline = getSearchDateSelectionFromForm({
          checkinDate: searchState.checkinDate,
          checkoutDate: searchState.checkoutDate,
          flexibleNights: searchState.flexibleNights,
          flexibleMonths: searchState.flexibleMonths,
          isAnytimeDateSelected: searchState.isAnytimeDateSelected,
        })
        refData.dateConfirmationAppliedSelection = undefined
        refData.dateConfirmationAcknowledgedSelection = undefined
        refData.dateConfirmationRequired = false
        refData.dateConfirmationViewed = false
        lastForcedExtractKeyRef.current = undefined
        // Allow new NL extracts to update form/URL dates after the user edited the query
        // (e.g. 10 oct → 11 oct then Search before/after extract).
        onIncompatibleSearchPhraseChange?.()
        if (refData.typeaheadId) {
          dispatch(setTypeaheadUserSelectedDates({
            typeaheadId: refData.typeaheadId,
            dates: undefined,
          }))
        }
      } else {
        refData.dateConfirmationPhrase = searchPhrase
      }
    }

    setSearchPhrase(searchPhrase)
    onPhraseChange?.(searchPhrase)
  }, [onPhraseChange, onIncompatibleSearchPhraseChange, searchState.checkinDate, searchState.checkoutDate, searchState.flexibleNights, searchState.flexibleMonths, searchState.isAnytimeDateSelected, temporarySearchSubmitRef, dispatch])

  return <SearchPlaceSelect
    className={className}
    label={label}
    placeholder={placeholder}
    data-testid={props['data-testid']}
    value={searchItem}
    searchTypes={searchTypes}
    placeTypes={placeTypes}
    onChange={onLocationSelect}
    onToggle={onLocationToggle}
    onResultsChange={onResultsChange}
    placeholderItems={localizedPopularItems}
    placeholderTitle={intl.formatMessage({
      id: 'mmfDEIOrgFaPfRp4MBqR',
      defaultMessage: 'Popular places',
      description: 'Heading for popular destination suggestions in search location input',
    })}
    variant={variant}
    dropdownSize={dropdownSize}
    dropdownPortaled={dropdownPortaled}
    inputRef={inputRef}
    required={required}
    searchVertical={searchVertical}
    recentSearchItems={recentSearchItems}
    overrideSearchItems={overrideSearchItems}
    controlsContainerRef={controlsContainerRef}
    onClear={handleClear}
    onPhraseChange={handleSearchPhraseChange}
    onSearchPhraseChange={onSearchPhraseChange}
    multiline={multiline}
    showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
    temporarySearchSubmitRef={temporarySearchSubmitRef}
    searchPath={searchPath}
    searchMapPath={searchMapPath}
  />
}
export default SearchLocationInput
